import React, { useState } from 'react';
import { Alert, Button, Input, Modal, Radio, Space, Typography } from 'antd';

import TurnstileWidget from './TurnstileWidget';
import { reportSharedPage } from '../../services/shareLinks';
import { REPORT_DETAILS_MAX, REPORT_REASONS } from '../../utils/shareReports';

const { Text } = Typography;

/**
 * "Report this page" (PR 10c, owner ruling G16): the quiet link at the foot
 * of every public page, and the small form behind it — a reason from the
 * fixed list and, optionally, a few words. No account is needed; the report
 * goes to keepr's admins (`POST /api/share/{code}/report`), who can revoke the
 * link, and nobody else is told. A sent report is a plain thank-you.
 */
const describeShareReportError = failure => (failure?.response?.status === 404
	? 'This link no longer works, so there is nothing left to report.'
	: "The report couldn't be sent. Try again in a few minutes.");

/**
 * KPR-514 widened it for a PERMANENT public form's "Report this form": a
 * report there can pause the form, so each one costs a bot check. The caller
 * passes `onReport({ reason, text, turnstileToken })`, its own `describeError`
 * and `challenge: { siteKey }` — and the dialog renders a Turnstile widget of
 * its OWN, mounted only while it is open. Rejected: borrowing the form's
 * widget (SharedFormPage's), whose token the Send button is holding — a
 * token is single use, and a report that spent it would fail the visitor's
 * next send. `challenge: { siteKey: '' }` (a build with no key) says the
 * report cannot be sent here rather than sending one the API must refuse.
 */
export default function ReportPageLink({
	code,
	className = '',
	label = 'Report this page',
	onReport = null,
	describeError = describeShareReportError,
	challenge = null,
	thanks = 'Thank you. Someone at keepr will look at this page.',
	prompt = 'What is wrong with this page? Your report goes to keepr, not to whoever shared it.',
}) {
	const [open, setOpen] = useState(false);
	const [reason, setReason] = useState(null);
	const [details, setDetails] = useState('');
	const [sending, setSending] = useState(false);
	const [sent, setSent] = useState(false);
	const [error, setError] = useState(null);
	const [token, setToken] = useState(null);
	const [resetKey, setResetKey] = useState(0);
	const needsChallenge = Boolean(challenge);
	const siteKey = challenge?.siteKey || '';

	const close = () => {
		setOpen(false);
		setReason(null);
		setDetails('');
		setSent(false);
		setError(null);
		setToken(null);
	};

	const send = async () => {
		if (!reason) return;
		if (needsChallenge && !token) return;
		setSending(true);
		setError(null);
		try {
			if (onReport) await onReport({ reason, text: details, turnstileToken: token || undefined });
			else await reportSharedPage(code, { reason, text: details });
			setSent(true);
		} catch (failure) {
			setError(describeError(failure));
			// The token went with the attempt, passed or not: ask for a new one.
			if (needsChallenge) {
				setToken(null);
				setResetKey(n => n + 1);
			}
		} finally {
			setSending(false);
		}
	};

	const footer = sent
		? [<Button key="close" type="primary" onClick={close}>Close</Button>]
		: [
			<Button key="cancel" onClick={close}>Cancel</Button>,
			<Button key="send" type="primary" loading={sending} disabled={!reason || (needsChallenge && !token)} onClick={send}>Send report</Button>,
		];

	return (
		<>
			<Button type="link" size="small" className={`report-page__link${className ? ` ${className}` : ''}`} onClick={() => setOpen(true)}>
				{label}
			</Button>
			<Modal open={open} title={label} onCancel={close} footer={footer} destroyOnClose className="report-page">
				{sent ? (
					<p className="report-page__thanks" data-testid="report-page-thanks">
						{thanks}
					</p>
				) : (
					<Space direction="vertical" size="middle" className="report-page__form">
						<Text type="secondary">{prompt}</Text>
						<Radio.Group
							aria-label="Reason"
							className="report-page__reasons"
							value={reason}
							onChange={event => setReason(event.target.value)}
						>
							<Space direction="vertical">
								{REPORT_REASONS.map(option => (
									<Radio key={option.value} value={option.value}>{option.label}</Radio>
								))}
							</Space>
						</Radio.Group>
						<label className="report-page__label" htmlFor="report-page-details">Anything else (optional)</label>
						<Input.TextArea
							id="report-page-details"
							value={details}
							maxLength={REPORT_DETAILS_MAX}
							showCount
							autoSize={{ minRows: 2, maxRows: 5 }}
							onChange={event => setDetails(event.target.value)}
						/>
						{needsChallenge && siteKey ? (
							<TurnstileWidget siteKey={siteKey} action="report" resetKey={resetKey} onToken={setToken} />
						) : null}
						{needsChallenge && !siteKey ? (
							<Alert
								type="warning"
								showIcon
								message="This page cannot run keepr's bot check here, so a report cannot be sent from it right now."
							/>
						) : null}
						{needsChallenge && siteKey && !token && !error ? (
							<Text type="secondary" className="report-page__hint">Checking you are not a bot…</Text>
						) : null}
						{error ? <Text type="danger" role="alert">{error}</Text> : null}
					</Space>
				)}
			</Modal>
		</>
	);
}
